import { isPowerOperator } from "@corca/core";

import { OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP } from "../mappings";

import {
  classifyInfix,
  containsMatrixNode,
  isTransposeExponent,
  op,
  operatorSymbolString,
  SympyBuildError,
} from "./shared";

import type { FactorToken, TermSign } from "./shared";
import type { SympyBuilder } from "./SympyBuilder";
import type { InfixKind } from "../mappings";
import type { SemanticExpr } from "../types";
import type { NodeJson } from "@corca/data";

/* -------------------------------------------------------------------------- */
/* Inline folding into Add / Mul / Frac / Neg                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fold a list of factor tokens (atoms separated by `*`/`/` and implicit adjacency) into a single
 * SemanticExpr, or null when there is nothing to fold. Each division wraps everything folded so far
 * as the numerator of a new Frac, which is what makes `÷` left-associative.
 */
export function foldFactors(tokens: FactorToken[]): SemanticExpr | null {
  let result: SemanticExpr | null = null;
  let mulRun: SemanticExpr[] = [];
  let pendingDiv = false;

  const flushMul = () => {
    if (mulRun.length === 0) return;
    const factors = result === null ? mulRun : [result, ...mulRun];
    result = factors.length === 1 ? factors[0] : op("Mul", factors);
    mulRun = [];
  };

  for (const tok of tokens) {
    if (tok.kind === "div") {
      flushMul();
      if (result === null) throw new SympyBuildError("Division is missing a numerator");
      pendingDiv = true;
    } else if (pendingDiv) {
      if (result === null) throw new SympyBuildError("Division without a numerator");
      result = op("Frac", [result, tok.expr]);
      pendingDiv = false;
    } else {
      mulRun.push(tok.expr);
    }
  }
  flushMul();

  return result;
}

/**
 * Fold an inline node's children into an additive operator tree (Add of signed terms), or null when
 * there is nothing there (an empty inline). Set operators must already have been split off by
 * `trySetOperators`; any other non-arithmetic infix operator has no SymPy meaning and throws.
 */
export function foldInline(
  builder: SympyBuilder,
  children: NodeJson[],
  level: number,
): SemanticExpr | null {
  type Term = { sign: TermSign; factors: FactorToken[] };
  // Atoms (with their pending sign) split at top-level +/-; within a term, factors split at *//.
  const terms: Term[] = [];

  const newTerm = (sign: TermSign): Term => {
    const term: Term = { sign, factors: [] };
    terms.push(term);
    return term;
  };
  const currentOrNew = (): Term => (terms.length > 0 ? terms[terms.length - 1] : newTerm("plus"));

  let sawAtom = false;
  // True when an operand is expected rather than a binary operator (e.g. at the start of the inline or after a `+`/`-`).
  let expectOperand = true;
  // Accumulated unary minus to apply to the next atom (toggles so stacked signs like `−−` cancel).
  let pendingNeg = false;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const nextChild = children[i + 1];
    const isNextPower = isPowerOperator(nextChild);

    if (child.type === "operatorSymbol") {
      const kind = classifyInfix(builder, child);
      if (kind !== "plus" && kind !== "minus" && kind !== "mul" && kind !== "div") {
        throw new SympyBuildError(`Unsupported operator: ${operatorSymbolString(child)}`);
      }
      if (kind === "plus" || kind === "minus") {
        if (expectOperand) {
          if (kind === "minus") pendingNeg = !pendingNeg;
        } else {
          newTerm(kind);
        }
      } else if (kind === "div") {
        currentOrNew().factors.push({ kind: "div" });
      }
      // mul: explicit multiplication separator — factors of the current term simply concatenate.
      expectOperand = true;
      continue;
    }

    // Non-operator atom.
    const term = currentOrNew();

    // Implicit multiplication is handled by appending another factor to the term.
    let atom: SemanticExpr;
    if (isNextPower) {
      const baseExpr = builder.traverseNode(child, level + 1);
      if (baseExpr === null) throw new SympyBuildError("Empty power base");
      if (isTransposeExponent(nextChild)) {
        atom = op("Transpose", [baseExpr]);
      } else {
        const exponentExpr = builder.traverseNode(nextChild.children[0], level + 1);
        // An empty exponent renders as nothing, so `x^` is just `x`.
        if (exponentExpr === null) {
          atom = baseExpr;
        } else {
          atom = containsMatrixNode(child)
            ? op("MatPow", [baseExpr, exponentExpr])
            : op("Pow", [baseExpr, exponentExpr]);
        }
      }
      i += 1;
    } else {
      const traversed = builder.traverseNode(child, level + 1);
      // Nothing there (an empty group or placeholder cell) — a term can't be built from it.
      if (traversed === null) throw new SympyBuildError("Empty subexpression");
      atom = traversed;
    }

    if (pendingNeg) {
      atom = op("Neg", [atom]);
      pendingNeg = false;
    }
    term.factors.push({ kind: "factor", expr: atom });
    sawAtom = true;
    expectOperand = false;
  }

  if (!sawAtom && terms.length === 0) {
    return null;
  }

  const termExprs: SemanticExpr[] = terms.map((t) => {
    const folded = foldFactors(t.factors);
    // A term with no factors at all — a dangling `+`/`-` with nothing after it.
    if (folded === null) throw new SympyBuildError("Missing operand");
    return t.sign === "minus" ? op("Neg", [folded]) : folded;
  });

  if (termExprs.length === 1 && terms[0].sign === "plus") {
    return termExprs[0];
  }
  return op("Add", termExprs);
}

/**
 * Set operators, loosest-binding first: `∩` binds tighter than `∪` (as `×` does over `+`), and `∖`
 * tightest of the three.
 */
const SET_OPERATORS: ReadonlyArray<{ kind: InfixKind; sympyName: string }> = [
  { kind: "union", sympyName: "Union" },
  { kind: "intersection", sympyName: "Intersection" },
  { kind: "setdiff", sympyName: "Complement" },
];

function isInfixKind(builder: SympyBuilder, node: NodeJson, kind: InfixKind): boolean {
  return node.type === "operatorSymbol" && classifyInfix(builder, node) === kind;
}

/**
 * Fold set operators into their SymPy constructors. Structured rather than infix because `∖` has no
 * Python operator meaning set difference: `-` folds into arithmetic `A + (−B)`, which asks SymPy to
 * *add* the sets.
 *
 * Splitting on the loosest operator present, at its last occurrence, yields both the precedence
 * above and left associativity.
 *
 * @example `A ∖ B ∪ C` → `Union(Complement(A, B), C)`
 * @example `A ∖ B ∖ C` → `Complement(Complement(A, B), C)`
 */
function trySetOperators(
  builder: SympyBuilder,
  children: NodeJson[],
  level: number,
): SemanticExpr | null {
  for (const { kind, sympyName } of SET_OPERATORS) {
    let lastIdx = -1;
    for (let i = 0; i < children.length; i++) {
      if (isInfixKind(builder, children[i], kind)) lastIdx = i;
    }
    if (lastIdx === -1) continue;

    const left = foldInlineChildren(builder, children.slice(0, lastIdx), level);
    const right = foldInlineChildren(builder, children.slice(lastIdx + 1), level);
    if (left === null || right === null) {
      throw new SympyBuildError(`Missing operand for ${operatorSymbolString(children[lastIdx])}`);
    }
    return op(sympyName, [left, right]);
  }
  return null;
}

/** Fold a run of inline children, or null when they render to nothing. */
export function foldInlineChildren(
  builder: SympyBuilder,
  children: NodeJson[],
  level: number,
): SemanticExpr | null {
  return trySetOperators(builder, children, level) ?? foldInline(builder, children, level);
}

export function foldInlineNode(
  builder: SympyBuilder,
  node: NodeJson,
  level: number,
): SemanticExpr | null {
  const relationIndices: number[] = [];
  node.children.forEach((n, i) => {
    if (
      n.type === "operatorSymbol" &&
      n.children[0]?.type === "symbol" &&
      OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP[n.children[0].props.key ?? ""]
    ) {
      relationIndices.push(i);
    }
  });

  if (relationIndices.length >= 2) {
    throw new SympyBuildError(
      `Chained relational operators are not supported (found ${relationIndices.length} relations)`,
    );
  }

  if (relationIndices.length === 1) {
    const index = relationIndices[0];
    const relNode = node.children[index];
    const symbolKey =
      relNode.type === "operatorSymbol" && relNode.children[0]?.type === "symbol"
        ? (relNode.children[0].props.key ?? "")
        : "";
    const sympyFunc = OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP[symbolKey];

    // An empty side (a trailing `x =` still being typed) is dropped, as it renders to nothing.
    const args = [node.children.slice(0, index), node.children.slice(index + 1)]
      .map((side) => foldInlineChildren(builder, side, level))
      .filter(Boolean);

    return op(sympyFunc, args);
  }

  return foldInlineChildren(builder, node.children, level);
}
